import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import RadioChecked from "../../../../assets/radio-button-checked.svg";
import { ReactComponent as TranslateIcon } from "../../../../assets/translate.svg";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  Stack,
  TextField,
  FormControlLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  RadioGroup,
  Radio,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { usePortal } from "../../../../context/PortalsContext";
import { getS3PresignedUrl } from "../../../../services/api/apiClient";
import { GetAllGroupsResult } from "../../../../services/api/types/portalGroup/portalGroup";
import {
  GetS3PresignedUrlResult,
  PresignFileType,
} from "../../../../services/api/types/portalConfiguration/uploadPhoto";
import Toast from "../../../../utils/Toast";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../utils/inputLimits";
import { UploadFile } from "../../../Assignments/AssignmentUploads/components/UploadFile";
import { TranslateFieldModal } from "../../../Assignments/AssignmentUploads/components/TranslateFieldModal";
import { brand } from "../../../../theme/themePrimitives";

interface AddNewGroupModalProps {
  name: string;
  nameError: string;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  nameTranslations: Record<string, string>;
  setNameTranslations: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  openAddNewGroupModal: boolean;
  setOpenAddNewGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  buttonLoading: boolean;
  addNewGroup: () => void;
  fetchPortalGroups: GetAllGroupsResult[];
  setThumbnailURL: React.Dispatch<React.SetStateAction<string | null>>;
  setParentPortalGroupID: React.Dispatch<React.SetStateAction<string | null>>;
}

export const AddNewGroupModal: React.FC<AddNewGroupModalProps> = ({
  name,
  nameError,
  handleNameChange,
  nameTranslations,
  setNameTranslations,
  openAddNewGroupModal,
  setOpenAddNewGroupModal,
  modalTitle,
  buttonLoading,
  addNewGroup,
  fetchPortalGroups,
  setThumbnailURL,
  setParentPortalGroupID,
}) => {
  const [nameTranslateModalOpen, setNameTranslateModalOpen] = useState(false);
  const [isSubGroup, setIsSubGroup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [parentGroupError, setParentGroupError] = useState(false);
  const { portals } = usePortal();

  const [portalName, setPortalName] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  const handleImageUpload = async (resizedFile: File) => {
    try {
      setLoading(true);
      const response: GetS3PresignedUrlResult = await getS3PresignedUrl(
        portals.selectedPortal?.portalUid!,
        {
          fileName: resizedFile.name,
          contentType: resizedFile.type,
          fileType: PresignFileType.GROUP_THUMBNAIL,
        },
      );
      const { uploadUrl, path } = response;
      setThumbnailURL(path);
      await axios.put(uploadUrl, resizedFile, {
        headers: {
          "Content-Type": resizedFile.type,
        },
      });
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(error.response.data);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailChange = (file: File | null) => {
    if (!file) return;
    setThumbnailFile(file);
    handleImageUpload(file);
  };

  const handlePortalNameChange = (event: any, child: any) => {
    const selectedName = event.target.value;
    const selectedId = (child?.props as any)["data-portalid"];
    setPortalName(selectedName);
    setParentPortalGroupID(selectedId);
  };

  const handleAddNewGroup = () => {
    if (isSubGroup && !portalName) {
      setParentGroupError(true);
      return;
    }
    addNewGroup();
  };

  useEffect(() => {
    if (!openAddNewGroupModal) {
      setThumbnailFile(null);
      setIsSubGroup(false);
      setPortalName("");
      setParentPortalGroupID(null);
      setThumbnailURL(null);
      setParentGroupError(false);
    }
  }, [openAddNewGroupModal, setParentPortalGroupID, setThumbnailURL]);

  return (
    <>
      <Modal
        open={openAddNewGroupModal}
        onClose={() => setOpenAddNewGroupModal(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
            padding: "16px",
            width: "50%",
          }}
        >
          {errorMessage && (
            <Toast
              open={Boolean(errorMessage)}
              message={errorMessage}
              duration={2000}
              onClose={() => setErrorMessage("")}
              type={"error"}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #D0D5DD",
              pb: 2,
              mb: 1,
            }}
          >
            <Typography variant="h5">{modalTitle}</Typography>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={() => setOpenAddNewGroupModal(false)}
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Stack>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2">
                  Name
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <TranslateIcon
                  onClick={() =>
                    name.trim() !== "" && setNameTranslateModalOpen(true)
                  }
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "2px",
                    borderRight:
                      name.trim() !== ""
                        ? `1px solid ${brand[600]}`
                        : "1px solid #98A2B3",
                    borderBottom:
                      name.trim() !== ""
                        ? `1px solid ${brand[600]}`
                        : "1px solid #98A2B3",
                    cursor: name.trim() !== "" ? "pointer" : "default",
                    color: name.trim() !== "" ? brand[600] : "#98A2B3",
                  }}
                />
              </Box>
              <TextField
                fullWidth
                id="name"
                placeholder="Enter group name"
                type="text"
                value={name}
                onChange={handleNameChange}
                variant="outlined"
                size="small"
                error={Boolean(nameError)}
                helperText={nameError}
                slotProps={{
                  input: {
                    endAdornment: nameError && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                  htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
                }}
              />
              <Stack sx={{ flexDirection: "row", gap: 2 }}>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  sx={{ width: "100%" }}
                >
                  <FormControlLabel
                    value="Main group"
                    control={
                      <Radio
                        onChange={() => {
                          setIsSubGroup(false);
                          setPortalName("");
                          setParentPortalGroupID(null);
                        }}
                        checked={!isSubGroup}
                        icon={
                          <RadioButtonUncheckedOutlinedIcon
                            style={{
                              color: "lightgray",
                            }}
                          />
                        }
                        checkedIcon={<img src={RadioChecked} alt="" />}
                      />
                    }
                    label="Main group"
                    sx={{ flex: 1 }}
                  />
                  <FormControlLabel
                    value="Sub group"
                    control={
                      <Radio
                        checked={isSubGroup}
                        onChange={() => setIsSubGroup((prev) => !prev)}
                        icon={
                          <RadioButtonUncheckedOutlinedIcon
                            style={{
                              color: "lightgray",
                            }}
                          />
                        }
                        checkedIcon={<img src={RadioChecked} alt="" />}
                      />
                    }
                    label="Sub group"
                    sx={{ flex: 1 }}
                  />
                </RadioGroup>
              </Stack>
              {isSubGroup && (
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: "5px" }}>
                    Main group
                    <Box component="span" sx={{ color: `var(--error-700)` }}>
                      *
                    </Box>
                  </Typography>
                  <Select
                    id="add-new-group-portal-select"
                    size="small"
                    value={portalName}
                    onChange={(e, child) => {
                      handlePortalNameChange(e, child);
                      setParentGroupError(false);
                    }}
                    displayEmpty
                    error={parentGroupError}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    <MenuItem disabled value="">
                      Select main group
                    </MenuItem>
                    {fetchPortalGroups.map((group: GetAllGroupsResult) => (
                      <MenuItem
                        key={group.PortalGroupUid}
                        value={group.name}
                        data-portalid={group.PortalGroupUid}
                      >
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {parentGroupError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      Please select a main group
                    </Typography>
                  )}
                </Stack>
              )}
              {loading ? (
                <Box
                  sx={{
                    minHeight: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              ) : (
                <UploadFile
                  value={thumbnailFile}
                  onChange={handleThumbnailChange}
                  mode="thumbnail"
                  disabled={loading}
                  label="Thumbnail"
                  labelVariant="body2"
                />
              )}
              <Divider sx={{ my: "15px !important" }} />
              <Stack>
                {buttonLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Button
                    onClick={handleAddNewGroup}
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: `${brand[600]} !important`,
                      color: `white`,
                      "&:hover": {
                        bgcolor: `${brand[700]} !important`,
                        fontWeight: 700,
                      },
                    }}
                  >
                    <Typography>Add new group card</Typography>
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <TranslateFieldModal
        open={nameTranslateModalOpen}
        onClose={() => setNameTranslateModalOpen(false)}
        fieldLabel="Group name"
        englishValue={name}
        onEnglishValueChange={(value) =>
          handleNameChange({
            target: { value },
          } as React.ChangeEvent<HTMLInputElement>)
        }
        translations={nameTranslations}
        onSave={setNameTranslations}
      />
    </>
  );
};
